import { Auth } from "@aws-amplify/auth";
import { snackbarService } from "@one/web-components/dist/index.cjs.js";

const buildLucenePayload = ({
  searchValue,
  filterQuery,
  searchText,
  isSearching,
  queryOptions,
  start,
  size,
  sort,
  sortBy,
  sourceFields,
}) => {
  const finalQuery = [searchValue, filterQuery]
    .filter(Boolean)
    .map((q) => `(${q})`)
    .join(" AND ");

  const MAX_OR_CLAUSES = 990;
  const countOrClauses = (str = "") => (str.match(/\sOR\s/g) || []).length;
  const totalOrClauses = countOrClauses(finalQuery || "");

  if (totalOrClauses > MAX_OR_CLAUSES) {
    snackbarService.show({
      message: "Too many conditions, please select less options",
      type: "error",
      duration: 5000,
    });
  }

  const fields = isSearching ? queryOptions : undefined;

  const query = isSearching
    ? {
      bool: {
        must: [
          {
            query_string: {
              query: `*${searchText}*`,
              analyze_wildcard: true,
              default_operator: "AND",
              fields,
            },
          },
          ...(finalQuery ? [{ query_string: { query: finalQuery } }] : []),
        ],
      },
    }
    : {
      query_string: {
        query: finalQuery || "*", // Default to match all if empty
      },
    };

  return {
    from: start,
    size,
    track_total_hits: true,
    query,
    ...(sortBy && {
      sort: [{ [sortBy]: { order: sort } }],
    }),
    ...(sourceFields && { _source: sourceFields }),
  };
};

/**
 * Builds the Query Object for raw Query DSL
 */
const buildQueryDSLPayload = (
  searchValue,
  start,
  size,
  sort,
  sortBy,
  queryOptions
) => {
  const parsedQuery =
    typeof searchValue === "string" ? JSON.parse(searchValue) : searchValue;

  const queryBody = {
    from: start,
    size,
    track_total_hits: true,
    query: parsedQuery,
  };

  if (sortBy && !queryBody.sort) {
    queryBody.sort = [{ [sortBy]: { order: sort } }];
  }

  if (queryOptions?.fields && Array.isArray(queryOptions.fields) && !queryBody._source) {
    queryBody._source = queryOptions.fields;
  }

  return queryBody;
};

/**
 * Normalizes the OpenSearch response
 */
const processSearchResults = (data) => {
  const hits = data.hits?.hits || [];
  const count = data.hits?.total?.value || 0;

  const fields = hits.map((item) => ({
    ...item._source,
    id: item._id,
  }));

  return { data: fields, count };
};

const getEmptyResult = () => ({ data: [], count: 0 });

/**
 * Main Exported Function
 */
export const OpenSearchConfig = async ({
  start = 0,
  size = 10,
  searchValue,
  sort = "asc",
  sortBy,
  filterQuery,
  queryOptions = [],
  isSearching = false,
  searchText = "",
  queryParser = "lucene",
  sourceFields,
}) => {
  try {
    const session = await Auth.currentSession();
    const authorizationid = session.getIdToken().getJwtToken();

    // 1. Generate the JSON payload instead of a URL string
    const payload =
      queryParser === "lucene"
        ? buildLucenePayload({
          searchValue,
          filterQuery,
          start,
          size,
          sort,
          sortBy,
          isSearching,
          queryOptions,
          searchText,
          sourceFields,
        })
        : buildQueryDSLPayload(searchValue, start, size, sort, sortBy, queryOptions);

    const endpoint = `${process.env.REACT_APP_AWS_OPENSEARCH_ENDPOINT}/_search`;

    // 2. Perform the POST request
    const response = await fetch(endpoint, {
      method: "POST", // Changed to POST
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${authorizationid}`,
      },
      body: JSON.stringify(payload), // Send the query in the body
    });

    if (!response.ok) {
      return getEmptyResult();
    }

    const data = await response.json();
    return processSearchResults(data);
  } catch (error) {
    console.error("API error:", error);
    return getEmptyResult();
  }
};
